import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../components/Toast';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const FaturalandirmaPage = () => {
  const [faturalar, setFaturalar] = useState([]);
  const [siparisler, setSiparişler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ siparis_id: '', fatura_no: '', tutar: '', kdv_tutar: '' });
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const token = localStorage.getItem('access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadData = useCallback(async () => {
    try {
      const [fatRes, sipRes] = await Promise.all([
        fetch(`${API}/api/faturalar`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/siparisler`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (fatRes.ok) setFaturalar(await fatRes.json());
      if (sipRes.ok) setSiparişler(await sipRes.json());
    } catch (err) {
      logError('Veri yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const tutar = parseFloat(formData.tutar) || 0;
      const kdv = parseFloat(formData.kdv_tutar) || tutar * 0.08;
      const siparis = siparisler.find(s => s.id === parseInt(formData.siparis_id));

      const body = {
        siparis_id: parseInt(formData.siparis_id),
        fatura_no: formData.fatura_no || `FTR-${new Date().getFullYear()}-${String(faturalar.length + 1).padStart(5, '0')}`,
        tedarikci_id: siparis?.tedarikci_id || 1,
        fatura_tarihi: new Date().toISOString().slice(0, 10),
        vade_tarihi: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        tutar: tutar,
        kdv_tutar: kdv,
      };

      const res = await fetch(`${API}/api/faturalar`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) {
        toast.success('Fatura başarıyla oluşturuldu!');
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl']);
        setShowForm(false);
        setFormData({ siparis_id: '', fatura_no: '', tutar: '', kdv_tutar: '' });
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fatura oluşturulamadı');
      }
    } catch (err) {
      toast.error('Bağlantı hatası');
    } finally {
      setSubmitting(false);
    }
  };

  const toplamFatura = faturalar.reduce((s, f) => s + (f.toplam || 0), 0);
  const odenmisFatura = faturalar.filter(f => f.durum === 'ODENDI');
  const odenmemisFatura = faturalar.filter(f => f.durum === 'ODENECEK');
  const faturasizSiparis = siparisler.filter(s =>
    s.durum === 'TESLIM_ALINDI' && !faturalar.some(f => f.siparis_id === s.id)
  );

  const getDurumBadge = (durum) => {
    const map = {
      'ODENECEK': { bg: '#fff3e0', color: '#e65100' },
      'KISMI_ODENDI': { bg: '#e3f2fd', color: '#1565c0' },
      'ODENDI': { bg: '#e8f5e9', color: '#2e7d32' },
      'IPTAL': { bg: '#ffebee', color: '#d32f2f' },
    };
    const s = map[durum] || { bg: '#f5f5f5', color: colors.textSecondary };
    return <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: radius.xl, fontSize: '12px', fontWeight: '500', background: s.bg, color: s.color }}>{durum}</span>;
  };

  const styles = {
    container: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    title: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    kpiCard: (color) => ({
      background: colors.surface, borderRadius: radius.lg, padding: '20px',
      borderLeft: `4px solid ${color}`, boxShadow: shadows.md
    }),
    kpiLabel: { fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' },
    kpiValue: (color) => ({ fontSize: '28px', fontWeight: '700', color }),
    kpiSub: { fontSize: '12px', color: colors.textTertiary, marginTop: '4px' },
    card: { background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.md, overflow: 'hidden', marginBottom: '20px' },
    cardTitle: { padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '12px 16px', textAlign: 'left', background: colors.backgroundAlt, color: colors.textSecondary, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: `2px solid ${colors.border}` },
    td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '14px' },
    btn: (bg, color) => ({ padding: '8px 16px', borderRadius: radius.md, border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px', background: bg, color }),
    formCard: { background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.md, padding: '24px', marginBottom: '20px' },
    formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
    formLabel: { fontSize: '13px', fontWeight: '600', color: '#555' },
    formInput: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.md, fontSize: '14px', outline: 'none' },
    formSelect: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.md, fontSize: '14px', outline: 'none', background: colors.surface },
    alert: (type) => ({
      padding: '12px 16px', borderRadius: radius.md, marginBottom: '16px', fontSize: '14px',
      background: type === 'success' ? '#e8f5e9' : '#ffebee',
      color: type === 'success' ? '#2e7d32' : '#d32f2f',
      border: `1px solid ${type === 'success' ? '#c8e6c9' : '#ffcdd2'}`
    }),
  };

  if (loading && faturalar.length === 0 && siparisler.length === 0) return <div style={styles.container}><p>Yükleniyor...</p></div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Faturalandırma</h1>
        <button style={styles.btn('#1a1a2e', '#fff')} onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Kapat' : '+ Yeni Fatura'}
        </button>
      </div>


      {/* KPI Cards */}
      <div style={styles.kpiRow}>
        <div style={styles.kpiCard('#1976d2')}>
          <div style={styles.kpiLabel}>Toplam Fatura</div>
          <div style={styles.kpiValue('#1976d2')}>{faturalar.length}</div>
          <div style={styles.kpiSub}>{toplamFatura.toLocaleString('tr-TR')} ₺</div>
        </div>
        <div style={styles.kpiCard('#f57c00')}>
          <div style={styles.kpiLabel}>Ödenmemiş</div>
          <div style={styles.kpiValue('#f57c00')}>{odenmemisFatura.length}</div>
          <div style={styles.kpiSub}>{odenmemisFatura.reduce((s, f) => s + (f.toplam || 0), 0).toLocaleString('tr-TR')} ₺</div>
        </div>
        <div style={styles.kpiCard('#388e3c')}>
          <div style={styles.kpiLabel}>Ödenmiş</div>
          <div style={styles.kpiValue('#388e3c')}>{odenmisFatura.length}</div>
        </div>
        <div style={styles.kpiCard('#d32f2f')}>
          <div style={styles.kpiLabel}>Faturasız Sipariş</div>
          <div style={styles.kpiValue('#d32f2f')}>{faturasizSiparis.length}</div>
          <div style={styles.kpiSub}>Teslim alınmış</div>
        </div>
      </div>

      {/* Yeni Fatura Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', marginBottom: '16px' }}>Yeni Fatura Oluştur</h3>
          <form onSubmit={handleSubmit}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Sipariş</label>
                <select style={styles.formSelect} value={formData.siparis_id}
                  onChange={(e) => {
                    const sid = e.target.value;
                    const sip = siparisler.find(s => s.id === parseInt(sid));
                    setFormData({ ...formData, siparis_id: sid, tutar: sip?.toplam_tutar || '' });
                  }}>
                  <option value="">Sipariş Seçin</option>
                  {siparisler.filter(s => s.durum === 'TESLIM_ALINDI').map(s => (
                    <option key={s.id} value={s.id}>
                      {s.siparis_no} - {s.tedarikci_unvan || `T-${s.tedarikci_id}`} ({(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺)
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Fatura No (opsiyonel)</label>
                <input style={styles.formInput} placeholder="Otomatik oluşturulur" value={formData.fatura_no}
                  onChange={(e) => setFormData({ ...formData, fatura_no: e.target.value })} />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Tutar (₺)</label>
                <input style={styles.formInput} type="number" step="0.01" required value={formData.tutar}
                  onChange={(e) => setFormData({ ...formData, tutar: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>KDV Tutarı (₺)</label>
                <input style={styles.formInput} type="number" step="0.01" placeholder="Otomatik %8"
                  value={formData.kdv_tutar}
                  onChange={(e) => setFormData({ ...formData, kdv_tutar: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button type="submit" disabled={submitting} style={styles.btn('#1976d2', '#fff')}>
                {submitting ? 'Oluşturuluyor...' : 'Fatura Oluştur'}
              </button>
              <button type="button" style={styles.btn('#f0f0f0', '#555')} onClick={() => setShowForm(false)}>İptal</button>
            </div>
          </form>
        </div>
      )}

      {/* Fatura Listesi */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }}>Fatura Listesi</span>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Fatura No</th>
              <th style={styles.th}>Tedarikçi</th>
              <th style={styles.th}>Fatura Tarihi</th>
              <th style={styles.th}>Vade</th>
              <th style={styles.th}>Tutar</th>
              <th style={styles.th}>KDV</th>
              <th style={styles.th}>Toplam</th>
              <th style={styles.th}>Durum</th>
            </tr>
          </thead>
          <tbody>
            {faturalar.length === 0 ? (
              <tr><td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: colors.textTertiary, padding: '40px' }}>Fatura bulunamadı</td></tr>
            ) : faturalar.map(f => (
              <tr key={f.id}>
                <td style={{ ...styles.td, fontWeight: '600' }}>{f.fatura_no}</td>
                <td style={styles.td}>{f.tedarikçi || '-'}</td>
                <td style={styles.td}>{f.fatura_tarihi || '-'}</td>
                <td style={styles.td}>{f.vade || '-'}</td>
                <td style={styles.td}>{(f.tutar || 0).toLocaleString('tr-TR')} ₺</td>
                <td style={styles.td}>{(f.kdv || 0).toLocaleString('tr-TR')} ₺</td>
                <td style={{ ...styles.td, fontWeight: '700' }}>{(f.toplam || 0).toLocaleString('tr-TR')} ₺</td>
                <td style={styles.td}>{getDurumBadge(f.durum)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FaturalandirmaPage;
